/**
 * Chart configuration validation utilities.
 * Validates chart configs before rendering to catch errors early.
 */

import type {
  ChartConfig,
  ChartType,
  ColumnTypeInfo,
  AggregatedColumnMapping,
  BarChartVariant,
  AreaChartVariant,
} from '../types/chartTypes';

/** Validation error with context */
export type ValidationError = {
  field: string;
  message: string;
};

/** Validation result */
export type ValidationResult = {
  valid: boolean;
  errors: ValidationError[];
};

/**
 * Creates a validation error
 */
function error(field: string, message: string): ValidationError {
  return { field, message };
}

/**
 * Validates that a required field is present
 */
function validateRequired(
  value: unknown,
  field: string,
  label: string
): ValidationError | null {
  if (value === null || value === undefined) {
    return error(field, `${label} is required`);
  }
  if (typeof value === 'string' && value.trim() === '') {
    return error(field, `${label} is required`);
  }
  return null;
}

/**
 * Validates X-axis configuration
 */
function validateXAxis(config: ChartConfig): ValidationError[] {
  const errors: ValidationError[] = [];

  const requiredError = validateRequired(
    config.xAxis?.column,
    'xAxis.column',
    'X-axis column'
  );
  if (requiredError) errors.push(requiredError);

  return errors;
}

/**
 * Validates Y-axis configuration
 */
function validateYAxis(
  config: ChartConfig,
  columnInfos?: ColumnTypeInfo[]
): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!config.yAxis || config.yAxis.length === 0) {
    errors.push(error('yAxis', 'At least one Y-axis column is required'));
    return errors;
  }

  // For pie charts, only one Y column is allowed
  if (config.type === 'pie' && config.yAxis.length > 1) {
    errors.push(error('yAxis', 'Pie charts support only one Y-axis column'));
  }

  // Scatter charts support only one Y column
  if (config.type === 'scatter' && config.yAxis.length > 1) {
    errors.push(error('yAxis', 'Scatter charts support only one Y-axis column'));
  }

  // Maximum 4 Y columns
  if (config.yAxis.length > 4) {
    errors.push(error('yAxis', 'Maximum 4 Y-axis columns allowed'));
  }

  // No duplicate Y columns
  const yColumnNames = config.yAxis.map((y) => y.column).filter(Boolean);
  if (new Set(yColumnNames).size < yColumnNames.length) {
    errors.push(error('yAxis', 'Duplicate Y-axis columns are not allowed'));
  }

  // Validate each Y column
  config.yAxis.forEach((yCol, index) => {
    if (!yCol.column || yCol.column.trim() === '') {
      errors.push(error(`yAxis[${index}].column`, 'Y-axis column is required'));
    }

    // If column infos provided, validate that Y columns are numeric
    if (columnInfos && yCol.column) {
      const colInfo = columnInfos.find((c) => c.key === yCol.column);
      if (colInfo && colInfo.category !== 'numeric') {
        errors.push(
          error(
            `yAxis[${index}].column`,
            `Column "${yCol.column}" should be numeric for Y-axis`
          )
        );
      }
    }
  });

  return errors;
}

/**
 * Validates scatter chart requirements
 */
function validateScatterChart(
  config: ChartConfig,
  columnInfos?: ColumnTypeInfo[]
): ValidationError[] {
  const errors: ValidationError[] = [];

  if (config.type !== 'scatter') return errors;

  // Scatter charts require numeric X-axis
  if (columnInfos && config.xAxis?.column) {
    const xColInfo = columnInfos.find((c) => c.key === config.xAxis.column);
    if (xColInfo && xColInfo.category !== 'numeric') {
      errors.push(
        error(
          'xAxis.column',
          'Scatter charts require a numeric X-axis column'
        )
      );
    }
  }

  return errors;
}

/**
 * Validates series configuration
 */
function validateSeries(
  config: ChartConfig,
  columnInfos?: ColumnTypeInfo[]
): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!config.series?.column) return errors;

  // Series column should be categorical (string or boolean)
  if (columnInfos) {
    const seriesInfo = columnInfos.find(
      (c) => c.key === config.series?.column
    );
    if (
      seriesInfo &&
      seriesInfo.category !== 'string' &&
      seriesInfo.category !== 'boolean'
    ) {
      errors.push(
        error(
          'series.column',
          'Series column should be categorical (text or boolean)'
        )
      );
    }
  }

  // Pie charts don't support series grouping
  if (config.type === 'pie') {
    errors.push(error('series', 'Pie charts do not support series grouping'));
  }

  return errors;
}

/**
 * Validates that column references exist in the available columns
 */
function validateColumnReferences(
  config: ChartConfig,
  columnInfos: ColumnTypeInfo[]
): ValidationError[] {
  const errors: ValidationError[] = [];
  const availableColumns = new Set(columnInfos.map((c) => c.key));

  // Check X-axis column
  if (config.xAxis?.column && !availableColumns.has(config.xAxis.column)) {
    errors.push(
      error('xAxis.column', `Column "${config.xAxis.column}" not found`)
    );
  }

  // Check Y-axis columns
  config.yAxis?.forEach((yCol, index) => {
    if (yCol.column && !availableColumns.has(yCol.column)) {
      errors.push(
        error(`yAxis[${index}].column`, `Column "${yCol.column}" not found`)
      );
    }
  });

  // Check series column
  if (
    config.series?.column &&
    !availableColumns.has(config.series.column)
  ) {
    errors.push(
      error('series.column', `Column "${config.series.column}" not found`)
    );
  }

  return errors;
}

/**
 * Validates a complete chart configuration
 */
export function validateChartConfig(
  config: ChartConfig,
  columnInfos?: ColumnTypeInfo[]
): ValidationResult {
  const errors: ValidationError[] = [];

  // Basic type validation
  const typeError = validateRequired(config.type, 'type', 'Chart type');
  if (typeError) errors.push(typeError);

  // X-axis validation
  errors.push(...validateXAxis(config));

  // Y-axis validation
  errors.push(...validateYAxis(config, columnInfos));

  // Scatter-specific validation
  errors.push(...validateScatterChart(config, columnInfos));

  // Series validation
  errors.push(...validateSeries(config, columnInfos));

  // Column reference validation (if column info available)
  if (columnInfos && columnInfos.length > 0) {
    errors.push(...validateColumnReferences(config, columnInfos));
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Checks if a chart type is valid
 */
export function isValidChartType(type: unknown): type is ChartType {
  return (
    typeof type === 'string' &&
    ['bar', 'line', 'pie', 'area', 'scatter'].includes(type)
  );
}

/**
 * Checks if a bar chart variant is valid
 */
export function isValidBarVariant(variant: unknown): variant is BarChartVariant {
  return (
    typeof variant === 'string' &&
    ['grouped', 'stacked'].includes(variant)
  );
}

/**
 * Checks if an area chart variant is valid
 */
export function isValidAreaVariant(variant: unknown): variant is AreaChartVariant {
  return (
    typeof variant === 'string' &&
    ['overlapping', 'stacked'].includes(variant)
  );
}

/**
 * Creates a default chart configuration for a given type
 */
export function createDefaultChartConfig(
  type: ChartType,
  xColumn: string,
  yColumns: AggregatedColumnMapping[],
  options?: {
    barVariant?: BarChartVariant;
    areaVariant?: AreaChartVariant;
  }
): ChartConfig {
  return {
    type,
    xAxis: { column: xColumn },
    yAxis: yColumns,
    showLegend: true,
    showGrid: type !== 'pie',
    barVariant: type === 'bar' ? (options?.barVariant ?? 'grouped') : undefined,
    areaVariant: type === 'area' ? (options?.areaVariant ?? 'overlapping') : undefined,
  };
}

/**
 * Suggests a chart type based on column types
 */
export function suggestChartType(columnInfos: ColumnTypeInfo[]): ChartType {
  const numericCount = columnInfos.filter(
    (c) => c.category === 'numeric'
  ).length;
  const dateCount = columnInfos.filter((c) => c.category === 'date').length;
  const stringCount = columnInfos.filter((c) => c.category === 'string').length;

  // If we have dates, suggest line chart (time series)
  if (dateCount > 0 && numericCount > 0) {
    return 'line';
  }

  // If we have 2+ numeric columns, could use scatter
  if (numericCount >= 2) {
    return 'scatter';
  }

  // If we have categories and values, bar chart is good
  if (stringCount > 0 && numericCount > 0) {
    return 'bar';
  }

  // Default to bar
  return 'bar';
}

/**
 * Checks if a config has meaningful data for charting
 */
export function hasChartableData(
  rowCount: number,
  columnInfos: ColumnTypeInfo[]
): { chartable: boolean; reason?: string } {
  if (rowCount === 0) {
    return { chartable: false, reason: 'No data to chart' };
  }

  const numericColumns = columnInfos.filter((c) => c.category === 'numeric');
  if (numericColumns.length === 0) {
    return { chartable: false, reason: 'No numeric columns for chart values' };
  }

  return { chartable: true };
}
